import InvoiceHomeDetailCard from "./InvoiceHomeDetailCard";
import { getInvoiceDetailsCardInfo } from "../invoice-master/invoice-helper";
import { RootState } from "src/context/store";
import { useSelector, useDispatch } from "react-redux";
import { useCallback, useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Spinner } from "src/components";
import { invoiceActions } from "src/context/invoice-slice";
import {
  fetchFuidRejectionInvoicesData,
} from "src/services/invoice-api";
import { useToast } from "src/hooks/useToast";
import useInvoiceSearchGate, { INVOICE_SEARCH_PROMPT } from "src/hooks/useInvoiceSearchGate";

const InvoiceFuidRejectionTab = ({ searchValue, searchCategory }: { searchValue?: string; searchCategory?: string }) => {
  const { invoiceFuidRejectionData, invoiceAnalysisData } = useSelector((state: RootState) => state.invoice);
  const [hasMore, setHasMore] = useState(true);
  const { InvoiceFilters } = useSelector((state: RootState) => state.invoice);
  const [page, setPage] = useState(2);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { canFetch, awaitingCriteria } = useInvoiceSearchGate(InvoiceFilters, searchValue); // M10-16144

  const company_code = InvoiceFilters?.company?.map((item) => item.code)?.join(",") || "";

  const searchParams = useMemo(
    () => ({
      company_code,
      division_code: InvoiceFilters?.division?.map((item) => item.company_divisioncode)?.join(",") || "",
      startDate: InvoiceFilters?.dateRange?.from,
      endDate: InvoiceFilters?.dateRange?.to,
      assignee_id: InvoiceFilters?.assignee?.map((item) => item.invoice_assignment_id)?.join(",") || "",
      city: InvoiceFilters?.city?.map((item) => item.city_name?.split("|")?.[0]?.trim())?.join(",") || "",
      district: InvoiceFilters?.district?.map((item) => item.district_name)?.join(",") || "",
      state: InvoiceFilters?.state?.map((item) => item.state_name)?.join(",") || "",
      rejection_code: InvoiceFilters?.rejection?.map((item) => item.rejection_code)?.join(",") || "",
      ordering: InvoiceFilters?.sorting?.[0]?.key,
      fuid_status_id:
        InvoiceFilters?.status?.length || 0 > 0
          ? InvoiceFilters?.status?.map((item) => item.fuid_status_id).join(",") || ""
          : "",
      page_size: 10,
      page_number: 1,
      screen: "Entry",
      dateType: InvoiceFilters?.dateType?.[0]?.key || "",
      uploadType: InvoiceFilters?.uploadType?.[0]?.key,
      moduleType: InvoiceFilters?.moduleType?.[0]?.key,
      order_mode: InvoiceFilters?.orderMode?.[0]?.key,
      cndn_remarks_code_int: InvoiceFilters?.orderType?.[0]?.key,
      ...(searchValue && searchCategory ? { [searchCategory]: searchValue } : {}),
    }),
    [searchValue, searchCategory, InvoiceFilters],
  );

  const fetchTabData = useCallback(async () => {
    setIsLoading(true);
    dispatch(invoiceActions.setInvoiceDetails([]));
    dispatch(invoiceActions.setInvoicesTextractData([]));
    window.localStorage.removeItem("counterCompanyLocationId");
    window.localStorage.removeItem("counterOrderId");
    try {
      const res = await fetchFuidRejectionInvoicesData(searchParams);
      dispatch(invoiceActions.setInvoiceFuidRejectionData(res?.data?.data?.invoiceData || []));
      if (res?.data?.data) {
        dispatch(
          invoiceActions.setInvoiceAnalysisData({
            entry: {
              "All Invoices": invoiceAnalysisData?.entry?.["All Invoices"] || 0,
              "Priority Tasks": invoiceAnalysisData?.entry?.["Priority Tasks"] || 0,
              "Escalated": invoiceAnalysisData?.entry?.["Escalated"] || 0,
              "Rejection Review": res?.data?.data?.total || 0,
              "Feedback from Ext":
                res?.data?.data?.count?.count_kam_feedback_pending ??
                invoiceAnalysisData?.entry?.["Feedback from Ext"] ??
                0,
            },
            approval: {
              "All Invoices": invoiceAnalysisData?.approval?.["All Invoices"] || 0,
              "Priority Tasks": invoiceAnalysisData?.approval?.["Priority Tasks"] || 0,
              "Escalated": invoiceAnalysisData?.approval?.["Escalated"] || 0,
            },
          }),
        );
      }
      setPage(2);
      setHasMore(true);
    } catch (error) {
      console.error("Error fetching Rejection Review invoices:", error);
      toast({ description: "Failed to fetch Rejection Review invoices", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, searchParams]);

  const fetchMoreData = async () => {
    if (isLoading) return;
    try {
      const params = { ...searchParams, page_number: page, skip_counts: true }; // M10-16144: page N reads only invoiceData
      const response = await fetchFuidRejectionInvoicesData(params);
      const newData = response?.data?.data?.invoiceData;
      if (!newData || newData.length === 0) {
        setHasMore(false);
        return;
      }
      dispatch(invoiceActions.setInvoiceFuidRejectionData([...(invoiceFuidRejectionData || []), ...newData]));
      setPage((prev) => prev + 1);
    } catch (error) {
      console.error("Error fetching more Rejection Review invoices:", error);
      setHasMore(false);
    }
  };

  useEffect(() => {
    dispatch(invoiceActions.setInvoiceEntryTab(3));
    // M10-16144: roles outside the default-load list wait for a search/filter before fetching
    if (!canFetch) {
      dispatch(invoiceActions.setInvoiceFuidRejectionData([]));
      setHasMore(false);
      return;
    }
    fetchTabData();
  }, [fetchTabData, canFetch]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mt-10 w-full">
      {invoiceFuidRejectionData?.length === 0 && (
        <div className="flex justify-center items-center h-[20vh]">
          <p className="text-lg text-gray-500">{awaitingCriteria ? INVOICE_SEARCH_PROMPT : "No invoices pending your rejection review"}</p>
        </div>
      )}

      <div
        id="scrollableDiv"
        className="h-screen overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
      >
        <InfiniteScroll
          dataLength={invoiceFuidRejectionData?.length}
          next={fetchMoreData}
          hasMore={hasMore}
          loader={""}
          scrollableTarget="scrollableDiv"
          endMessage={awaitingCriteria ? null : <div className="py-4 text-center text-gray-500">No more invoices to load</div>}
        >
          <ul>
            {invoiceFuidRejectionData?.map((data: any, index: number) => {
              return (
                <li className="my-5 rounded-xl border shadow-sm border-gray-200" key={index}>
                  <InvoiceHomeDetailCard
                    status={data?.fileStatusHdr?.fuid_status_desc}
                    data={getInvoiceDetailsCardInfo(data, false)} //M10-15065
                    checkDataType={false}
                    invoiceData={data}
                    viewDetails={true}
                    index={index}
                    isFuidRejectionReview={true}
                    fetchTabData={fetchTabData}
                  />
                </li>
              );
            })}
          </ul>
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default InvoiceFuidRejectionTab;
